import React from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
  HStack,
  VStack,
  Icon,
  useColorModeValue,
  Tooltip,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { FaBox, FaSitemap, FaStore, FaLink, FaUnlink, FaExternalLinkAlt } from 'react-icons/fa';

function getGatewayColor(type) {
  if (type === 'basalam') return 'orange';
  if (type === 'digikala') return 'red';
  return 'purple';
}

function resolveLogicalVariantId(value) {
  if (!value) return null;
  if (typeof value === 'object' && value._id) return String(value._id);
  return String(value);
}

function buildGatewayProductLink(gw, ref, product) {
  if (!gw || !ref?.productId) return null;
  if (gw.type === 'digikala') return `https://www.digikala.com/product/dkp-${ref.productId}`;
  if (gw.type === 'basalam') {
    const storeId = gw.basalamStoreId || 'user';
    return `https://basalam.com/${storeId}/product/${ref.productId}`;
  }
  if (gw.type === 'custom' && gw.customSiteUrl && product?.accountingCode) {
    const baseUrl = gw.customSiteUrl.replace(/\/$/, '');
    return `${baseUrl}/p/${product.accountingCode}`;
  }
  return null;
}

function buildVariantTree(product) {
  const logicalVariants = product?.logicalVariants || [];
  const gatewayRefs = product?.gatewayRefs || [];

  const logicalNodes = logicalVariants.map((lv) => ({
    logicalVariant: lv,
    gatewayVariants: [],
  }));

  const logicalById = new Map(
    logicalNodes.map((node) => [String(node.logicalVariant._id), node])
  );

  const unmapped = [];
  const productLevelGateways = [];

  gatewayRefs.forEach((ref) => {
    const gw = ref.gateway || {};
    const variants = ref.variants || [];

    if (variants.length === 0) {
      productLevelGateways.push({ ref, gw });
      return;
    }

    variants.forEach((v) => {
      const entry = { ref, gw, variant: v };
      const lvId = resolveLogicalVariantId(v.logicalVariantId);

      if (lvId && logicalById.has(lvId)) {
        logicalById.get(lvId).gatewayVariants.push(entry);
      } else {
        unmapped.push(entry);
      }
    });
  });

  return { logicalNodes, unmapped, productLevelGateways };
}

function StockBadge({ stock, size = 'sm' }) {
  const color = stock > 10 ? 'green' : stock > 0 ? 'orange' : 'red';
  return (
    <Badge colorScheme={color} fontSize={size === 'xs' ? '10px' : 'xs'} px={2} py={0.5} borderRadius="md">
      {stock ?? 0} عدد
    </Badge>
  );
}

function TreeLine({ height = 'full', isLast = false }) {
  const lineColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  return (
    <Box position="relative" w="20px" flexShrink={0} alignSelf="stretch" minH={isLast ? '20px' : undefined}>
      <Box
        position="absolute"
        right="9px"
        top={0}
        bottom={isLast ? 'auto' : 0}
        h={isLast ? '12px' : height}
        w="2px"
        bg={lineColor}
      />
      <Box
        position="absolute"
        right="0"
        top="12px"
        w="12px"
        h="2px"
        bg={lineColor}
      />
    </Box>
  );
}

function GatewayVariantCard({ entry, product, compact = false }) {
  const { ref, gw, variant: v } = entry;
  const gwName = gw.name || 'درگاه نامشخص';
  const gwColor = getGatewayColor(gw.type);
  const link = buildGatewayProductLink(gw, ref, product);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue(`${gwColor}.100`, `${gwColor}.800`);
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'white');

  const label = v.name || v.sku || v.accountingCode || `تنوع ${v.variantId}`;

  return (
    <Flex
      align="center"
      gap={3}
      p={compact ? 2 : 3}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      borderRightWidth="3px"
      borderRightColor={`${gwColor}.400`}
    >
      <Badge colorScheme={gwColor} fontSize="10px" flexShrink={0}>
        {gw.type || 'custom'}
      </Badge>
      <Box flex={1} minW={0}>
        <Text fontSize="xs" fontWeight="bold" color={textColor} isTruncated title={label}>
          {label}
        </Text>
        <HStack spacing={2} mt={0.5} flexWrap="wrap">
          {v.variantId ? (
            <Text fontSize="10px" color={mutedColor} dir="ltr">
              ID: {v.variantId}
            </Text>
          ) : null}
          {(v.sku || v.accountingCode) && (
            <Text fontSize="10px" color={mutedColor} dir="ltr">
              SKU: {v.sku || v.accountingCode}
            </Text>
          )}
          {v.price > 0 && (
            <Text fontSize="10px" color={mutedColor}>
              {Number(v.price).toLocaleString('fa-IR')} ت
            </Text>
          )}
        </HStack>
      </Box>
      <VStack spacing={1} align="end" flexShrink={0}>
        <StockBadge stock={v.stock ?? 0} size="xs" />
        {link && (
          <Tooltip label={`مشاهده در ${gwName}`} hasArrow>
            <IconButton
              size="xs"
              variant="ghost"
              colorScheme="blue"
              icon={<FaExternalLinkAlt />}
              aria-label="مشاهده"
              onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
            />
          </Tooltip>
        )}
      </VStack>
    </Flex>
  );
}

function LogicalVariantNode({ node, product, isLast }) {
  const lv = node.logicalVariant;
  const connections = node.gatewayVariants;

  const nodeBg = useColorModeValue('teal.50', 'whiteAlpha.100');
  const nodeBorder = useColorModeValue('teal.200', 'teal.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const childAreaBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const emptyBorder = useColorModeValue('gray.200', 'whiteAlpha.200');

  return (
    <Flex gap={0} align="stretch">
      <TreeLine isLast={isLast} />
      <Box flex={1} pb={isLast ? 0 : 4}>
        <Flex
          p={3}
          bg={nodeBg}
          borderWidth="1px"
          borderColor={nodeBorder}
          borderRadius="xl"
          borderRightWidth="4px"
          borderRightColor="teal.400"
          align="center"
          justify="space-between"
          gap={3}
          mb={connections.length > 0 ? 2 : 0}
        >
          <HStack spacing={2} minW={0}>
            <Icon as={FaSitemap} color="teal.500" boxSize={3.5} flexShrink={0} />
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="bold" color={textColor} isTruncated>
                {lv.accountingCode || '(بدون کد)'}
              </Text>
              <Text fontSize="10px" color={mutedColor}>
                تنوع منطقی — انبار مرکزی
              </Text>
            </Box>
          </HStack>
          <StockBadge stock={lv.stock ?? 0} />
        </Flex>

        {connections.length > 0 ? (
          <Box mr={4} pl={3} borderRightWidth="2px" borderRightColor={nodeBorder} borderRightStyle="dashed">
            <VStack align="stretch" spacing={2} bg={childAreaBg} p={2} borderRadius="lg">
              <HStack spacing={1}>
                <Icon as={FaLink} color="blue.400" boxSize={3} />
                <Text fontSize="10px" fontWeight="bold" color={mutedColor}>
                  {connections.length} اتصال به درگاه
                </Text>
              </HStack>
              {connections.map((entry, i) => (
                <GatewayVariantCard key={entry.variant._id || `${entry.ref._id}-${i}`} entry={entry} product={product} compact />
              ))}
            </VStack>
          </Box>
        ) : (
          <Flex
            mr={4}
            p={2}
            borderWidth="1px"
            borderStyle="dashed"
            borderColor={emptyBorder}
            borderRadius="lg"
            align="center"
            gap={2}
          >
            <Icon as={FaUnlink} color="gray.400" boxSize={3} />
            <Text fontSize="xs" color={mutedColor}>
              هنوز به هیچ درگاهی متصل نشده
            </Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

export default function ProductVariantTree({ product }) {
  const { logicalNodes, unmapped, productLevelGateways } = buildVariantTree(product);

  const rootBg = useColorModeValue('blue.50', 'blue.900');
  const rootBorder = useColorModeValue('blue.200', 'blue.700');
  const sectionBg = useColorModeValue('white', 'gray.800');
  const sectionBorder = useColorModeValue('gray.100', 'whiteAlpha.200');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningBorder = useColorModeValue('orange.200', 'orange.700');
  const totalBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const totalLogicalStock = (product.logicalVariants || []).reduce((s, v) => s + (v.stock ?? 0), 0);
  const totalMapped = logicalNodes.reduce((s, n) => s + n.gatewayVariants.length, 0);
  const totalGatewayVariants = (product.gatewayRefs || []).reduce(
    (s, ref) => s + (ref.variants?.length || 0),
    0
  );

  const hasLogicalVariants = logicalNodes.length > 0;

  return (
    <VStack align="stretch" spacing={4}>
      {/* ریشه درخت — محصول */}
      <Flex
        p={4}
        bg={rootBg}
        borderWidth="1px"
        borderColor={rootBorder}
        borderRadius="xl"
        align="center"
        gap={3}
      >
        <Icon as={FaBox} color="blue.500" boxSize={5} />
        <Box flex={1}>
          <Text fontWeight="bold" fontSize="md" color={textColor}>
            {product.name}
          </Text>
          <HStack spacing={3} mt={1} flexWrap="wrap">
            {product.accountingCode && (
              <Text fontSize="xs" color={mutedColor} dir="ltr">
                SKU: {product.accountingCode}
              </Text>
            )}
            <Text fontSize="xs" color={mutedColor}>
              {(product.gatewayRefs || []).length} درگاه متصل
            </Text>
          </HStack>
        </Box>
        {!hasLogicalVariants && (
          <StockBadge stock={product.stock ?? 0} />
        )}
      </Flex>

      {hasLogicalVariants ? (
        <>
          {/* شاخه‌های تنوع منطقی */}
          <Box
            bg={sectionBg}
            borderWidth="1px"
            borderColor={sectionBorder}
            borderRadius="xl"
            p={4}
            overflow="hidden"
          >
            <HStack mb={4} spacing={2}>
              <Icon as={FaSitemap} color="teal.500" />
              <Text fontSize="sm" fontWeight="bold" color={textColor}>
                درخت تنوع‌ها
              </Text>
              <Badge colorScheme="teal" fontSize="10px">
                {logicalNodes.length} تنوع منطقی
              </Badge>
              <Badge colorScheme="blue" fontSize="10px">
                {totalMapped} از {totalGatewayVariants} متصل
              </Badge>
            </HStack>

            <VStack align="stretch" spacing={0}>
              {logicalNodes.map((node, i) => (
                <LogicalVariantNode
                  key={node.logicalVariant._id || i}
                  node={node}
                  product={product}
                  isLast={i === logicalNodes.length - 1 && unmapped.length === 0}
                />
              ))}
            </VStack>

            <Flex mt={3} p={3} bg={totalBg} borderRadius="lg" justify="space-between" fontWeight="bold">
              <Text fontSize="sm" color={mutedColor}>مجموع موجودی انبار مرکزی</Text>
              <Text fontSize="sm" color="blue.500">{totalLogicalStock} عدد</Text>
            </Flex>
          </Box>

          {/* تنوع‌های بدون مپ */}
          {unmapped.length > 0 && (
            <Box
              bg={warningBg}
              borderWidth="1px"
              borderColor={warningBorder}
              borderRadius="xl"
              p={4}
            >
              <HStack mb={3} spacing={2}>
                <Icon as={FaUnlink} color="orange.500" />
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  تنوع‌های درگاه بدون اتصال به انبار
                </Text>
                <Badge colorScheme="orange" fontSize="10px">{unmapped.length}</Badge>
              </HStack>
              <Text fontSize="xs" color={mutedColor} mb={3}>
                این تنوع‌ها در درگاه‌ها ثبت شده‌اند ولی به هیچ تنوع منطقی مپ نشده‌اند.
              </Text>
              <VStack align="stretch" spacing={2}>
                {unmapped.map((entry, i) => (
                  <GatewayVariantCard key={entry.variant._id || `unmapped-${i}`} entry={entry} product={product} />
                ))}
              </VStack>
            </Box>
          )}
        </>
      ) : (
        /* محصول بدون تنوع منطقی */
        <Box bg={sectionBg} borderWidth="1px" borderColor={sectionBorder} borderRadius="xl" p={4}>
          <HStack mb={3} spacing={2}>
            <Icon as={FaStore} color="purple.500" />
            <Text fontSize="sm" fontWeight="bold" color={textColor}>
              تنوع‌های درگاه (بدون تنوع منطقی)
            </Text>
          </HStack>

          {(product.gatewayRefs || []).length === 0 ? (
            <Text fontSize="sm" color={mutedColor} textAlign="center" py={4}>
              این محصول به هیچ درگاهی متصل نیست.
            </Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {(product.gatewayRefs || []).map((ref) => {
                const gw = ref.gateway || {};
                const gwName = gw.name || 'درگاه نامشخص';
                const gwColor = getGatewayColor(gw.type);
                const variants = ref.variants || [];
                const link = buildGatewayProductLink(gw, ref, product);

                return (
                  <Box key={ref._id || gwName}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <HStack>
                        <Badge colorScheme={gwColor}>{gw.type || 'custom'}</Badge>
                        <Text fontSize="sm" fontWeight="bold" color={textColor}>{gwName}</Text>
                        {ref.productId && (
                          <Text fontSize="xs" color={mutedColor} dir="ltr">
                            PID: {ref.productId}
                          </Text>
                        )}
                      </HStack>
                      {link && (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          icon={<FaExternalLinkAlt />}
                          aria-label="مشاهده"
                          onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                        />
                      )}
                    </Flex>
                    {variants.length > 0 ? (
                      <VStack align="stretch" spacing={2} mr={2}>
                        {variants.map((v, i) => (
                          <GatewayVariantCard
                            key={v._id || i}
                            entry={{ ref, gw, variant: v }}
                            product={product}
                            compact
                          />
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color={mutedColor} mr={2}>
                        تنوعی ثبت نشده — موجودی سطح محصول: {product.stock ?? 0}
                      </Text>
                    )}
                    <Divider mt={3} borderColor={sectionBorder} />
                  </Box>
                );
              })}
            </VStack>
          )}

          {productLevelGateways.length > 0 && (
            <Text fontSize="xs" color={mutedColor} mt={2}>
              {productLevelGateways.length} درگاه بدون تنوع جداگانه
            </Text>
          )}
        </Box>
      )}
    </VStack>
  );
}
